import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Route, Routes, Link } from "react-router-dom";
import { Menu } from 'lucide-react';
import AboutUs from "./components/AboutUs";
import Asc46 from "./components/Asc46";
import Dto43a from "./components/Dto43a";
import Dto43b from "./components/Dto43b";
import Dto43b2 from "./components/Dto43b2";
import Dto43c from "./components/Dto43c";
import Dto43c2 from "./components/Dto43c2";
import Dto43d from "./components/Dto43d";
import Dto43d2 from "./components/Dto43d2";
import Dto43e from "./components/Dto43e";
import Dto43e2 from "./components/Dto43e2";
import Hcp3 from "./components/Hcp3";
import Mkd45 from "./components/Mkd45";
import Mt5 from "./components/Mt5";
import Sc4f from "./components/Sc4f";
import Taipeiims from "./components/Taipeiims";
import Ts41 from "./components/Ts41";
import Ts42 from "./components/Ts42";
import ContactUs from "./components/ContactUs";
import "./App.css";

const App: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    setActiveDropdown(null);
  };

  const toggleDropdown = (menu: string) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const handleLinkClick = () => {
    setMenuOpen(false);
    setActiveDropdown(null);
  };

  return (
    <Router>
      <div className="container">
        <header className="navbar" ref={navRef}>
          <button className="hamburger" onClick={toggleMenu} aria-label="Toggle menu">
            <Menu size={24} />
          </button>
          <nav className={`nav-menu ${menuOpen ? "open" : ""}`}>
            <ul>
              <li><Link to="/aboutus" onClick={handleLinkClick}>About Us</Link></li>

              <li className={`dropdown ${activeDropdown === "critical" ? "open" : ""}`}>
                <span onClick={() => toggleDropdown("critical")}>Critical Care</span>
                <ul className={`dropdown-content ${activeDropdown === "critical" ? "show" : ""}`}>
                  <li><Link to="/asc46" onClick={handleLinkClick}>BNCT Success Stories</Link></li>
                  <li><Link to="/mkd45" onClick={handleLinkClick}>BNCT Technology Database</Link></li>
                  <li><Link to="/ts41" onClick={handleLinkClick}>Heavy Ion Therapy</Link></li>
                  <li><Link to="/ts42" onClick={handleLinkClick}>Proton Therapy Treatment</Link></li>
                  <li><Link to="/hcp3" onClick={handleLinkClick}>Hospital & Clinic Partnerships</Link></li>
                  <li><Link to="/sc4f" onClick={handleLinkClick}>Advanced Cancer Treatment Success Stories</Link></li>
                </ul>
              </li>

              <li className={`dropdown ${activeDropdown === "dental" ? "open" : ""}`}>
                <span onClick={() => toggleDropdown("dental")}>Dental Services</span>
                <ul className={`dropdown-content ${activeDropdown === "dental" ? "show" : ""}`}>
                  <li><Link to="/dto43a" onClick={handleLinkClick}>Snow Porcelain R</Link></li>
                  <li><Link to="/dto43b" onClick={handleLinkClick}>Snow Porcelain Pro</Link></li>
                  <li><Link to="/dto43b2" onClick={handleLinkClick}>Enhanced Treatment Efficiency</Link></li>
                  <li><Link to="/dto43c" onClick={handleLinkClick}>Ceramic Veneers</Link></li>
                  <li><Link to="/dto43c2" onClick={handleLinkClick}>Expert Ceramic Veneer Care</Link></li>
                  <li><Link to="/dto43d" onClick={handleLinkClick}>Microendodontic Treatment</Link></li>
                  <li><Link to="/dto43d2" onClick={handleLinkClick}>Advanced Root Canal Treatment</Link></li>
                  <li><Link to="/dto43e" onClick={handleLinkClick}>Minimally Invasive Dental Implants</Link></li>
                  <li><Link to="/dto43e2" onClick={handleLinkClick}>Dental Implant Care Guide</Link></li>
                </ul>
              </li>

              <li className={`dropdown ${activeDropdown === "wellness" ? "open" : ""}`}>
                <span onClick={() => toggleDropdown("wellness")}>Mind-Body Wellness Services</span>
                <ul className={`dropdown-content ${activeDropdown === "wellness" ? "show" : ""}`}>
                  <li><Link to="/mt5" onClick={handleLinkClick}>Medical Tourism</Link></li>
                  <li><Link to="/taipeiims" onClick={handleLinkClick}>Taipei Global Medical Tourism</Link></li>
                </ul>
              </li>

              <li><Link to="/contactus" onClick={handleLinkClick}>ContactUs</Link></li>
            </ul>
          </nav>
        </header>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<AboutUs />} />
            <Route path="/aboutus" element={<AboutUs />} />
            <Route path="/asc46" element={<Asc46 />} />
            <Route path="/dto43a" element={<Dto43a />} />
            <Route path="/dto43b" element={<Dto43b />} />
            <Route path="/dto43b2" element={<Dto43b2 />} />
            <Route path="/dto43c" element={<Dto43c />} />
            <Route path="/dto43c2" element={<Dto43c2 />} />
            <Route path="/dto43d" element={<Dto43d />} />
            <Route path="/dto43d2" element={<Dto43d2 />} />
            <Route path="/dto43e" element={<Dto43e />} />
            <Route path="/dto43e2" element={<Dto43e2 />} />
            <Route path="/hcp3" element={<Hcp3 />} />
            <Route path="/mkd45" element={<Mkd45 />} />
            <Route path="/mt5" element={<Mt5 />} />
            <Route path="/sc4f" element={<Sc4f />} />
            <Route path="/taipeiims" element={<Taipeiims />} />
            <Route path="/ts41" element={<Ts41 />} />
            <Route path="/ts42" element={<Ts42 />} />
            <Route path="/contactus" element={<ContactUs />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
};

export default App;